/**
 * The document head for the comparison pages.
 *
 * No `Product` or `Review` markup, and that is a deliberate omission rather
 * than a gap. Both vocabularies assert a rating of a named thing, and Google
 * treats self-serving review markup — a company rating itself against a
 * competitor — as a manual-action-grade violation. The pages carry `FAQPage`
 * and `BreadcrumbList`, which describe what is actually on them.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import {
  alternates,
  breadcrumbJsonLd,
  canonical,
  faqJsonLd,
  organizationJsonLd,
  socialMeta,
} from "../seo";
import { COMPARE_SLUGS, comparePath } from "./index";
import type { CompareCopy, CompareSlug } from "./types";
import type { CompareUi } from "./ui";

/** The comparison hub. A new URL — v1 had the leaves but no parent. */
export function compareIndexPath(lang: ContentLang = "en"): string {
  return lang === "en" ? "/compare" : `/${lang}/compare`;
}

export function compareHead(
  slug: CompareSlug,
  lang: ContentLang,
  copy: CompareCopy,
  ui: CompareUi,
) {
  const path = comparePath(slug, lang);
  return {
    meta: [
      { title: copy.title },
      { name: "description", content: copy.description },
      ...socialMeta({
        title: copy.title,
        description: copy.description,
        path,
        lang,
        imageAlt: copy.h1,
      }),
      { "script:ld+json": faqJsonLd([...copy.faq]) },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path: compareIndexPath(lang) },
          { name: copy.h1, path },
        ]),
      },
      { "script:ld+json": organizationJsonLd() },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates((l) => comparePath(slug, l)),
    ],
  };
}

export function compareIndexHead(
  lang: ContentLang,
  ui: CompareUi,
  allCopy: Record<CompareSlug, CompareCopy>,
) {
  const path = compareIndexPath(lang);
  return {
    meta: [
      { title: ui.indexTitle },
      { name: "description", content: ui.indexDescription },
      ...socialMeta({
        title: ui.indexTitle,
        description: ui.indexDescription,
        path,
        lang,
        imageAlt: ui.indexH1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: ui.indexH1,
          description: ui.indexDescription,
          url: canonical(path),
          inLanguage: lang,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: COMPARE_SLUGS.length,
            itemListElement: COMPARE_SLUGS.map((slug, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: allCopy[slug].h1,
              url: canonical(comparePath(slug, lang)),
            })),
          },
        },
      },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path },
        ]),
      },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(compareIndexPath),
    ],
  };
}
